<template>
  <div class="p-4 flex flex-col items-center justify-center min-h-[300px]">
    <h1 class="text-2xl font-bold mb-6 text-gray-800">查看所有设备结果</h1>

    <!-- 下载按钮 -->
    <div class="parent-container">
      <button @click="handleDownload" class="download-btn">
        ⬇️ 下载设备结果文件
      </button>
    </div>
    <!-- 下载中弹窗 -->
    <div v-if="loading" class="loading-popup">
      <h2 class="text-xl font-bold mb-6">正在下载设备结果文件</h2>
      <div class="progress-bar-modern-container">
        <div class="progress-bar-modern-bg">
          <div
            class="progress-bar-modern"
            :style="{ width: downloadProgress + '%' }"
          ></div>
        </div>
        <div class="progress-info">
          <span>
            {{ humanSize(downloadedBytes) }} / {{ humanSize(totalBytes) }}
          </span>
          <span class="speed" v-if="downloadSpeed > 0">
            {{ formatSpeed(downloadSpeed) }}
          </span>
          <span class="percent">{{ downloadProgress }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { showError, showSuccess } from '../utils';

const axiosInstance = axios.create({
  baseURL: 'https://relia-server.com:3000',
  timeout: 120000,
  withCredentials: true,
});

export default {
  data() {
    return {
      loading: false,
      downloadProgress: 0,
      username: '',
      password: '',
      downloadedBytes: 0,
      totalBytes: 0,
      downloadSpeed: 0,
      _lastTime: 0,
      _lastBytes: 0,
      speedTimer: null,
    };
  },
  created() {
    this.username = localStorage.getItem('currentUser') || '';
    this.password = localStorage.getItem('currentPassword') || '';
  },
  methods: {
    formatSpeed(speedInMB) {
      if (speedInMB === 0) return '0 B/s';

      const speedInKB = speedInMB * 1024;
      if (speedInKB < 1024) {
        return speedInKB.toFixed(2) + ' KB/s';
      } else {
        return speedInMB.toFixed(2) + ' MB/s';
      }
    },
    async handleDownload() {
      this.loading = true;
      this.downloadProgress = 0;
      this.downloadedBytes = 0;
      this.totalBytes = 0;
      this.downloadSpeed = 0;
      this._lastTime = Date.now();
      this._lastBytes = 0;

      try {
        const response = await axiosInstance.get('/api/get_pack_result', {
          params: {
            username: this.username,
            password: this.password,
          },
          responseType: 'blob',
          onDownloadProgress: this.handleProgress,
        });

        const encodedFileName = response.headers['x-filename'];
        const fileName = encodedFileName ? decodeURIComponent(encodedFileName) : '设备结果文件.zip';

        const blob = new Blob([response.data], { type: 'application/zip' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);

        showSuccess('下载成功');
      } catch (err) {
        showError('下载失败');
        console.error(err);
      } finally {
        this.loading = false;
        clearInterval(this.speedTimer);
      }
    },
    handleProgress(event) {
      if (event.lengthComputable) {
        this.downloadedBytes = event.loaded;
        this.totalBytes = event.total;
        this.downloadProgress = Math.round((event.loaded * 100) / event.total);

        const now = Date.now();
        const dt = (now - this._lastTime) / 1000;
        if (dt > 0.2) {
          this.downloadSpeed = (event.loaded - this._lastBytes) / dt / (1024 * 1024); // MB/s
          this._lastTime = now;
          this._lastBytes = event.loaded;
        }
        console.log(`下载进度: ${this.downloadProgress}% (${this.humanSize(this.downloadedBytes)} / ${this.humanSize(this.totalBytes)})`);

         // ✅ 下载完成：等待 UI 渲染 100% 后再关闭
        if (event.loaded === event.total && !this._finishTimerSet) {
          this._finishTimerSet = true;

          // 等 Vue 渲染 DOM 后再延时隐藏
          this.$nextTick(() => {
            setTimeout(() => {
              this.loading = false;
              console.log('下载完成');
            }, 1000); // 可根据需要调整延时
          });
        }
      }
    },
    humanSize(bytes) {
      if (!bytes) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    },
  },
};
</script>

<style scoped>
.loading-popup {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; flex-direction: column;
  align-items: center; justify-content: flex-start;
  z-index: 9999; color: white;
  padding-top: 120px; /* 添加适当的顶部间距 */
}

.progress-bar-modern-container {
  width: 450px;
  background: rgba(255,255,255,0.05);
  border-radius: 18px;
  padding: 32px 32px 24px 32px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.progress-bar-modern-bg {
  width: 320px;      /* 比之前220px宽 */
  height: 20px;      /* 高度略微增大 */
  background: #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0,0,0,0.08);
  margin-bottom: 16px;
}

.progress-bar-modern {
  height: 100%;
  border-radius: 12px;
  background: linear-gradient(90deg, #4ADE80 0%, #2563EB 100%);
  transition: width 0.3s cubic-bezier(.4,2,.6,1);
  box-shadow: 0 2px 8px rgba(52,211,153,0.18);
}

.progress-info {
  width: 100%; /* 占据父容器全部宽度 */
  display: flex;
  justify-content: space-between; /* 关键修改：均匀分配空间 */
  align-items: center;
  font-size: 15px;
  color: #e0e7ef;
  font-weight: 500;
  letter-spacing: 0.01em;
  gap: 12px; 
  white-space: nowrap;
  flex-wrap: nowrap;
  padding: 4px 8px; /* 添加内边距防止边缘挤压 */
  border-radius: 4px; /* 仅用于调试，可移除 */
}

/* 移除所有margin-left: auto */
.progress-info .speed {
  color: #10b981;
  font-size: 14px;
  padding: 0 20px;
}

.progress-info .percent {
  color: #93c5fd;
  font-size: 14px;
  text-align: right;
}

/* 新增：防止第一个span挤压其他元素 */
.progress-info > span:first-child {
  flex-shrink: 1;
  min-width: 0; /* 允许文本截断 */
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 30px;
}

.parent-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
}
.parent-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh; /* 设置父容器的高度为视口高度 */
}


.download-btn {
  margin-top: 10px;
  padding: 22px 48px;
  font-size: 24px;
  font-weight: bold;
  color: white;
  background: linear-gradient(45deg, #6EE7B7, #3B82F6);
  border: none;
  border-radius: 999px;
  box-shadow: 0 6px 18px rgba(59,130,246,0.12);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(.4,2,.6,1);
  min-width: 220px;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.download-btn:hover {
  background: linear-gradient(45deg, #4ADE80, #2563EB);
  box-shadow: 0 12px 24px rgba(59,130,246,0.18);
  transform: translateY(-2px) scale(1.04);
}

.download-btn:active {
  background: linear-gradient(45deg, #34D399, #1D4ED8);
  box-shadow: 0 2px 8px rgba(59,130,246,0.18);
  transform: translateY(1px) scale(0.98);
}
</style>